<template>
  <div
    class="employee-column"
    :class="{ last: isLast }"
    :style="{
      display: 'grid',
      gridTemplateRows: `repeat(${timeMarks.length}, ${slotHeight}px)`,
      width: colWidth + 'px',
      minWidth: colWidth + 'px',
      maxWidth: colWidth + 'px'
    }"
  >
    <template v-for="(slot, idx) in slots" :key="'slot-' + idx">
      <div
        v-if="slot.type === 'break'"
        class="cell break-cell"
        :class="slot.breakClass"
        :style="{ gridRow: `span ${slot.rowspan}` }"
      >
        <span class="break-icon" :class="slot.breakClass">{{ slot.icon }}</span>
        <span>{{ slot.label }}</span>
      </div>

      <AppointmentPopover v-else-if="slot.type === 'appointment'" :appointment="slot.app">
        <div
          class="cell appointment-cell"
          :class="[slot.status, `status-${slot.app.status}`, { 'appointment-cell--short': slot.duration <= 15 }]"
          :style="{ gridRow: `span ${slot.rowspan}` }"
          @click="openViewModal(slot.app)"
        >
          <div class="appointment-client">{{ slot.client_name || 'Клиент удалён' }}</div>
          <div v-if="slot.duration > 15" class="appointment-phone">
            {{ slot.client_phone || '—' }}
          </div>
          <div v-if="slot.duration > 30" class="appointment-services">{{ formatServices(slot.services) }}</div>
          <div v-if="slot.duration > 45" class="appointment-status-label">{{ statusLabel(slot.app.status) }}</div>
        </div>
      </AppointmentPopover>

      <template v-else-if="slot.type === 'shift'">
        <div
          v-if="hasPermission('appointments.create')"
          class="cell shift-cell"
          :class="[slotShadeClass(idx), { 'slot-past': isSlotPast(slot) }]"
          @mouseenter="hoveredSlot = idx"
          @mouseleave="hoveredSlot = null"
          @click="openCreateModal(slot)"
        >
          <span class="cell-time">{{ slot.label }}</span>
          <span v-if="hoveredSlot === idx" class="add-appointment-tooltip">
            <span class="time-tooltip">{{ slot.label }}</span>Запись
          </span>
        </div>
        <div class="cell shift-cell disabled" :class="slotShadeClass(idx)" v-else>
          <span class="cell-time">{{ slot.label }}</span>
        </div>
      </template>

      <div v-else-if="slot.type === 'out'" class="cell out-of-shift" :class="slotShadeClass(idx)"></div>
    </template>

    <AppointmentCreateModal
      v-model:visible="showCreateModal"
      :date="currentDate"
      :time="modalStartTime"
      :employee="{ ...emp, ...activeShift, user_id: emp.user_id }"
      @close="closeModals"
    />

    <AppointmentViewModal
      v-if="showViewModal"
      :date="currentDate"
      :appointment="modalAppointment"
      @close="closeModals"
      @edit="openEditModal"
      @open-complete="openCompleteModal"
    />

    <AppointmentCompleteModal
      v-if="showCompleteModal"
      :appointment="modalAppointment"
      :date="currentDate"
      @close="showCompleteModal = false"
    />

    <AppointmentEditModal
      v-if="showEditModal"
      v-model:visible="showCreateModal"
      :date="currentDate"
      :appointment="modalAppointment"
      @close="closeModals"
    />
  </div>
</template>

<script>
import AppointmentPopover from './AppointmentPopover.vue'
import AppointmentViewModal from './AppointmentViewModal.vue'
import AppointmentEditModal from './AppointmentEditModal.vue'
import AppointmentCreateModal from './AppointmentCreateModal.vue'
import AppointmentCompleteModal from '@/components/util/calendar/AppointmentCompleteModal.vue'
import { mapState } from 'vuex'
import { hasPermission } from '@/components/util/accessHelper'

export default {
  name: 'ScheduleEmployeeColumn',
  components: {
    AppointmentCompleteModal,
    AppointmentPopover,
    AppointmentViewModal,
    AppointmentEditModal,
    AppointmentCreateModal
  },
  props: {
    emp: { type: Object, required: true },
    timeMarks: { type: Array, required: true },
    isLast: { type: Boolean, default: false },
    colWidth: { type: Number, default: 200 },
    slotHeight: { type: Number, default: 31 },
    appointments: { type: Array, default: () => [] },
    currentDate: { type: String, required: true }
  },
  data() {
    return {
      activeShift: null,
      showCompleteModal: false,
      hoveredSlot: null,
      showCreateModal: false,
      showViewModal: false,
      showEditModal: false,
      modalStartTime: '',
      modalAppointment: null
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    nowMin() {
      if (this.currentDate !== this.today) return null
      const now = new Date()
      return now.getHours() * 60 + now.getMinutes()
    },
    today() {
      return new Date().toISOString().slice(0, 10)
    },
    myAppointments() {
      return this.appointments
        .filter(
          app =>
            Number(app.user_id) === Number(this.emp.user_id)
        )
        .sort((a, b) => this.timeToMin(a.start_time) - this.timeToMin(b.start_time))
    },
    slots() {
      const slots = []
      const shifts = this.emp.shifts || []
      const appointments = this.myAppointments
      let i = 0
      while (i < this.timeMarks.length) {
        const t = this.timeMarks[i]
        const shift = shifts.find(s => t >= this.timeToMin(s.start_time) && t < this.timeToMin(s.end_time))
        const hasAppointment = appointments.some(a => this.timeToMin(a.start_time) === t)
        if (!shift && !hasAppointment) {
          slots.push({ type: 'out' })
          i++
          continue
        }

        const br = !hasAppointment && (shift?.breaks || []).find(b => this.timeToMin(b.start) === t)
        if (br) {
          const startIdx = i
          const endIdx = this.timeMarks.findIndex(tm => tm === this.timeToMin(br.end))
          const rowspan = endIdx > startIdx ? endIdx - startIdx : 1
          let icon = '⏸', breakClass = 'break'
          if (br.type === 'lunch') {
            icon = '🍽'
            breakClass = 'lunch'
          } else if (br.type === 'maintenance') {
            icon = '🛠'
            breakClass = 'maintenance'
          }
          slots.push({
            type: 'break',
            rowspan,
            label: this.breakLabel(br),
            icon,
            breakClass
          })
          i += rowspan
          continue
        }

        const appIdx = appointments.findIndex(a => this.timeToMin(a.start_time) === t)
        if (appIdx !== -1) {
          const app = appointments[appIdx]
          let duration = app.duration_time
          if (!duration) {
            duration = (app.services || []).reduce((sum, s) => sum + (s.duration || 0), 0)
          }
          duration = Number(duration) || 15
          duration = Math.ceil(duration / 15) * 15
          const startIdx = i
          let endIdx = startIdx + Math.round(duration / 15)
          const nextApp = appointments.find(a => this.timeToMin(a.start_time) > t)
          if (nextApp && this.timeToMin(nextApp.start_time) < this.timeMarks[endIdx]) {
            endIdx = this.timeMarks.findIndex(tm => tm === this.timeToMin(nextApp.start_time))
          }
          const rowspan = endIdx > startIdx ? endIdx - startIdx : 1
          const calcEnd = this.formatTime(this.timeMarks[startIdx] + duration)
          slots.push({
            type: 'appointment',
            rowspan,
            client_name: app.client_name,
            client_phone: app.client_phone,
            services: app.services,
            duration,
            status: this.appointmentStatus(app),
            start_time: app.start_time,
            calc_end: calcEnd,
            app,
            id: app.id
          })
          i += rowspan
          continue
        }

        slots.push({ type: 'shift', label: this.formatTime(t), shift })
        i++
      }
      return slots
    }
  },
  methods: {
    hasPermission,
    isSlotPast(slot) {
      try {
        if (!slot || typeof slot.label !== 'string' || !slot.label.includes(':')) return false
        const tz = this.user?.timezone || 'Europe/Moscow'
        const nowTz = new Date(new Date().toLocaleString('en-US', { timeZone: tz }))
        const yyyy = nowTz.getFullYear()
        const mm = String(nowTz.getMonth() + 1).padStart(2, '0')
        const dd = String(nowTz.getDate()).padStart(2, '0')
        const todayTz = `${yyyy}-${mm}-${dd}`
        const currentMinutes = nowTz.getHours() * 60 + nowTz.getMinutes()

        if (this.currentDate < todayTz) return true
        if (this.currentDate === todayTz) {
          const [h, m] = slot.label.split(':').map(Number)
          const slotMinutes = h * 60 + m
          return slotMinutes < currentMinutes
        }
        return false
      } catch (e) {
        return false
      }
    },
    timeToMin(str) {
      if (!str) return 0
      const [h, m] = str.split(':').map(Number)
      return h * 60 + m
    },
    breakLabel(br) {
      switch (br.type) {
        case 'lunch':
          return 'Обед'
        case 'maintenance':
          return 'Профилактика'
        case 'break':
          return 'Перерыв'
        default:
          return 'Перерыв'
      }
    },
    formatTime(t) {
      const h = Math.floor(t / 60)
      const m = t % 60
      return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
    },
    formatServices(services) {
      if (!Array.isArray(services) || !services.length) return ''
      const txt = services.map(s => s.name).join(', ')
      return txt.length > 22 ? `${txt.slice(0, 20)}…` : txt
    },
    slotShadeClass(idx) {
      return idx % 2 === 0 ? 'slot-dark' : 'slot-light'
    },
    appointmentStatus(app) {
      const now = new Date()
      const today = now.toISOString().slice(0, 10)
      const start = this.timeToMin(app.start_time)
      const end = this.timeToMin(app.end_time || '') || start
      const curr = now.getHours() * 60 + now.getMinutes()
      if (app.date < today || (app.date === today && end < curr)) return 'past'
      if (app.date === today && start <= curr && curr < end) return 'current'
      return 'future'
    },
    statusLabel(status) {
      const labels = {
        planned: 'Запланировано',
        confirmed: 'Подтверждено',
        done: 'Выполнено',
        no_show: 'Не пришёл',
        canceled: 'Отменено'
      }
      return labels[status] || 'Запланировано'
    },
    openCreateModal(slot) {
      if (!slot.shift) return
      this.activeShift = slot.shift
      if (this.isSlotPast(slot)) return
      this.modalStartTime = slot.label
      this.showCreateModal = true
    },
    openViewModal(app) {
      this.modalAppointment = app
      this.showViewModal = true
    },
    openEditModal() {
      this.showViewModal = false
      this.showEditModal = true
    },
    closeModals() {
      this.showCreateModal = false
      this.showViewModal = false
      this.showEditModal = false
      this.modalStartTime = ''
    },
    openCompleteModal() {
      this.showViewModal = false
      this.showCompleteModal = true
    }
  }
}
</script>

<style scoped>
.employee-column {
  box-sizing: border-box;
  position: relative;
  font-family: var(--ui-font-family-base);
}

.employee-column.last {
  border-right: none !important;
}

.cell {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent !important;
  box-sizing: border-box;
  position: relative;
}

.employee-column > .cell:first-child {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
  overflow: hidden;
}

.break-cell {
  background: var(--cal-bg-break) !important;
  border-left: 2px solid var(--cal-warning);
  color: var(--cal-text-primary);
  font-weight: 500;
}

.shift-cell .cell-time {
  display: none;
}

.out-of-shift {
  background: var(--cal-bg-out);
  border-color: transparent;
}

.appointment-cell {
  background:
    linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%),
    #334878 !important;
  color: var(--cal-text-inverse);
  border-radius: 0;
  font-size: 13px;
  padding: 0 10px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 31px;
  pointer-events: auto;
  cursor: pointer;
  border: none;
  transition: background 0.2s, filter 0.2s;
  margin: 0;
  font-weight: 400;
  text-align: center;
  box-shadow: none;
}

.popover-client.deleted {
  color: var(--cal-danger);
}

.appointment-cell.past {
  background: var(--cal-bg-appointment-past) !important;
  color: var(--cal-text-muted);
}

.appointment-cell.current {
  background: var(--cal-bg-appointment-current) !important;
  color: var(--cal-text-inverse);
  font-weight: 400;
}

.appointment-cell.status-planned,
.appointment-cell.past.status-planned,
.appointment-cell.current.status-planned,
.appointment-cell.future.status-planned {
  background:
    linear-gradient(rgba(0, 108, 254, 0.22), rgba(0, 108, 254, 0.22)),
    linear-gradient(299deg, #030b1b 13.22%, #15264e 86.78%),
    #334878 !important;
  color: var(--cal-text-inverse) !important;
}

.appointment-cell--short {
  min-height: 0;
  padding: 0 6px;
  justify-content: center;
}

.appointment-client {
  font-family: "Bahnschrift", var(--ui-font-family-base);
  font-size: 14.5px;
  line-height: 1.12;
  font-weight: 300;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.appointment-phone {
  font-family: "Bahnschrift", var(--ui-font-family-base);
  font-size: 12.5px;
  line-height: 1.15;
  color: #00bffa;
  font-weight: 700;
}

.appointment-cell--short .appointment-client {
  font-size: 14.5px;
  line-height: 1;
  font-weight: 300;
}

.appointment-services {
  display: none;
}

.appointment-status-label {
  margin-top: 4px;
  color: #020919;
  font-size: 13px;
  line-height: 1.1;
  font-weight: 700;
}

.add-appointment-tooltip {
  position: absolute;
  inset: 0;
  background: var(--cal-bg-hover);
  color: var(--cal-text-inverse);
  padding: 2px 10px;
  font-size: 12px;
  display: flex;
  white-space: nowrap;
  pointer-events: none;
  z-index: 20;
  align-items: center;
  justify-content: center;
}

.add-appointment-tooltip .time-tooltip {
  position: absolute;
  color: var(--cal-text-muted);
  left: 12px;
}

.shift-cell {
  background: var(--cal-bg-cell-alt) !important;
  position: relative;
  cursor: pointer;
}

.shift-cell.disabled {
  background: var(--cal-bg-cell-alt) !important;
  cursor: default;
  pointer-events: none;
  color: var(--cal-text-dim);
}

.shift-cell.slot-dark,
.out-of-shift.slot-dark,
.shift-cell.disabled.slot-dark {
  background: var(--cal-bg-cell-alt) !important;
}

.shift-cell.slot-light,
.out-of-shift.slot-light,
.shift-cell.disabled.slot-light {
  background: var(--cal-bg-cell) !important;
}

.break-cell.lunch {
  border-left-color: var(--cal-warning);
}

.break-cell.break {
  border-left-color: var(--cal-accent-strong);
}

.break-cell.maintenance {
  border-left-color: var(--cal-text-muted);
  color: var(--cal-text-regular);
}

.break-icon {
  font-size: 16px;
  margin-right: 6px;
  vertical-align: middle;
}

.slot-past {
  color: var(--cal-text-dim) !important;
  cursor: not-allowed !important;
  pointer-events: none;
}

.appointment-cell.status-done {
  background: linear-gradient(151deg, #174a63 0%, #0b1f2b 84%) !important;
}

.appointment-cell.status-no_show,
.appointment-cell.status-canceled {
  background: var(--cal-bg-appointment-past) !important;
}

.appointment-cell:hover {
  filter: brightness(1.05);
}
</style>

